import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Select,
  Heading,
  Text,
  Button,
  HStack,
  useColorModeValue,
  Grid,
  GridItem,
  VStack,
  useToast,
  Spinner,
  Input,
} from '@chakra-ui/react';
import { Card } from '../../../components/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { useWeeklySummary } from './useWeeklySummary';
import { SummaryOverview } from './components/SummaryOverview';
import { ActiveRocksList } from './components/ActiveRocksList';
import { CompletedItemsList } from './components/CompletedItemsList';
import { getWeekDateRange, formatDateRange, generateTextSummary } from './weeklySummary.util';
import { DownloadIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';

/**
 * Weekly Summary Page - Weekly activity summary and trend analysis
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function WeeklySummaryPage() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [selectedWeek, setSelectedWeek] = useState<Date>(new Date());
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [exportingPDF, setExportingPDF] = useState(false);
  const [dateRangeMode, setDateRangeMode] = useState<'week' | 'custom'>('week');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Calculate date range for selected week or custom range
  const { startDate, endDate } = useMemo(() => {
    if (dateRangeMode === 'custom' && customStartDate && customEndDate) {
      return {
        startDate: new Date(customStartDate),
        endDate: new Date(customEndDate),
      };
    }
    return getWeekDateRange(selectedWeek);
  }, [selectedWeek, dateRangeMode, customStartDate, customEndDate]);

  const {
    summary,
    trendData,
    activeRocks,
    completedItems,
    winsExcitedEntries,
    users,
    loading,
  } = useWeeklySummary(
    currentUser?.uid,
    startDate,
    endDate,
    selectedUserId !== 'all' ? selectedUserId : undefined
  );

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const filterLabelColor = useColorModeValue('gray.700', 'gray.300');
  const selectBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Generate week options (last 8 weeks)
  const weekOptions = useMemo(() => {
    const options: Array<{ label: string; value: Date }> = [];
    const today = new Date();
    for (let i = 0; i < 8; i++) {
      const weekDate = new Date(today);
      weekDate.setDate(today.getDate() - i * 7);
      const { startDate: weekStart } = getWeekDateRange(weekDate);
      options.push({
        label: formatDateRange(weekStart, getWeekDateRange(weekDate).endDate),
        value: weekDate,
      });
    }
    return options;
  }, []);

  const handleExportText = async () => {
    try {
      const generateWeeklySummary = httpsCallable(functionsUsEast1, 'generateWeeklySummary');
      
      const result = await generateWeeklySummary({
        userId: selectedUserId !== 'all' ? selectedUserId : currentUser?.uid,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: 'text',
      });

      const data = (result.data as any);
      if (data.format === 'text') {
        const blob = new Blob([data.data], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `weekly-summary-${format(startDate, 'yyyy-MM-dd')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
          title: 'Export successful',
          description: 'Weekly summary exported as text file',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error exporting text:', error);
      toast({
        title: 'Export failed',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      const generateWeeklySummary = httpsCallable(functionsUsEast1, 'generateWeeklySummary');
      
      const result = await generateWeeklySummary({
        userId: selectedUserId !== 'all' ? selectedUserId : currentUser?.uid,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: 'pdf',
      });

      const data = (result.data as any);
      if (data.format === 'pdf') {
        // Decode base64 PDF
        const pdfBlob = new Blob(
          [Uint8Array.from(atob(data.data), c => c.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `weekly-summary-${format(startDate, 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({
          title: 'Export successful',
          description: 'Weekly summary exported as PDF',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Export failed',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading weekly summary...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Weekly Summary
          </Heading>
          <Text color={subheadingColor}>
            {formatDateRange(startDate, endDate)}
          </Text>
        </Box>
        <HStack spacing={2}>
          <Button
            leftIcon={<DownloadIcon />}
            colorScheme="blue"
            variant="outline"
            onClick={handleExportText}
          >
            Export Text
          </Button>
          <Button
            leftIcon={exportingPDF ? <Spinner size="sm" /> : <DownloadIcon />}
            colorScheme="blue"
            variant="solid"
            onClick={handleExportPDF}
            isLoading={exportingPDF}
            loadingText="Generating PDF..."
          >
            Export PDF
          </Button>
        </HStack>
      </Flex>

      {/* Filters */}
      <Box mb={6}>
        <HStack spacing={4} mb={4}>
          <Box>
            <Text color={filterLabelColor} fontWeight="medium" mb={2}>
              Date Range Mode:
            </Text>
            <Select
              value={dateRangeMode}
              onChange={(e) => {
                setDateRangeMode(e.target.value as 'week' | 'custom');
                if (e.target.value === 'week') {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
              }}
              bg={selectBg}
              maxW="200px"
            >
              <option value="week">Week</option>
              <option value="custom">Custom Range</option>
            </Select>
          </Box>
          {dateRangeMode === 'week' ? (
            <Box>
              <Text color={filterLabelColor} fontWeight="medium" mb={2}>
                Select Week:
              </Text>
              <Select
                value={weekOptions.findIndex((opt) => 
                  format(opt.value, 'yyyy-MM-dd') === format(selectedWeek, 'yyyy-MM-dd')
                )}
                onChange={(e) => {
                  const index = parseInt(e.target.value);
                  setSelectedWeek(weekOptions[index].value);
                }}
                bg={selectBg}
                maxW="400px"
              >
                {weekOptions.map((opt, index) => (
                  <option key={index} value={index}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Box>
          ) : (
            <>
              <Box>
                <Text color={filterLabelColor} fontWeight="medium" mb={2}>
                  Start Date:
                </Text>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  bg={selectBg}
                  maxW="200px"
                />
              </Box>
              <Box>
                <Text color={filterLabelColor} fontWeight="medium" mb={2}>
                  End Date:
                </Text>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  bg={selectBg}
                  maxW="200px"
                />
              </Box>
            </>
          )}
          <Box>
            <Text color={filterLabelColor} fontWeight="medium" mb={2}>
              Filter by User:
            </Text>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              bg={selectBg}
              maxW="300px"
            >
              <option value="all">All Users</option>
              {users
                .filter((user) => user.isEOSUser)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
            </Select>
          </Box>
        </HStack>
      </Box>

      {/* Summary Overview */}
      <Box mb={8}>
        <SummaryOverview summary={summary} trendData={trendData || null} />
      </Box>

      {/* Trend Analysis */}
      {trendData && (
        <Card borderWidth="1px" borderColor={borderColor} mb={8}>
            <Heading size="md" mb={4} color={headingColor}>
              Trend Analysis
            </Heading>
            <VStack align="stretch" spacing={3}>
              <Text color={subheadingColor} fontSize="sm">
                Comparison with previous week ({formatDateRange(
                  getWeekDateRange(new Date(selectedWeek.getTime() - 7 * 24 * 60 * 60 * 1000)).startDate,
                  getWeekDateRange(new Date(selectedWeek.getTime() - 7 * 24 * 60 * 60 * 1000)).endDate
                )})
              </Text>
              {trendData.actionItemCompletionRate > 0 && (
                <Text color={subheadingColor} fontSize="sm">
                  Action Item Completion Rate: <Text as="span" fontWeight="bold">{trendData.actionItemCompletionRate}%</Text>
                </Text>
              )}
            </VStack>
        </Card>
      )}

      {/* Active Rocks and Completed Items */}
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6} mb={8}>
        <GridItem>
          <ActiveRocksList rocks={activeRocks} users={users} />
        </GridItem>
        <GridItem>
          <CompletedItemsList
            actionItems={completedItems.actionItems}
            issues={completedItems.issues}
            users={users}
          />
        </GridItem>
      </Grid>

      {/* Wins & Excited */}
      {winsExcitedEntries.length > 0 && (
        <Card borderWidth="1px" borderColor={borderColor}>
            <Heading size="md" mb={4} color={headingColor}>
              Wins & Excited ({winsExcitedEntries.length})
            </Heading>
            <VStack spacing={2} align="stretch">
              {winsExcitedEntries.map((entry) => (
                <Box
                  key={entry.id}
                  p={3}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Text fontSize="sm" color={subheadingColor} mb={1}>
                    {entry.type === 'win' ? 'Win' : 'Excited'}
                  </Text>
                  <Text color={headingColor}>{entry.content}</Text>
                </Box>
              ))}
            </VStack>
        </Card>
      )}
    </Container>
  );
}

